// Online play in the browser: one WebSocket to the room's Arena. Snapshots are buffered and played back
// INTERP_DELAY_MS behind the server, interpolating cells and viruses between the two snapshots around that
// moment (extrapolating at most 50 ms past the newest); cells that appear grow out of their nearest sibling and
// cells that vanish shrink away over that interval. Pellets live in a local store patched by the server's
// deltas. The result is the same Scene the solo host builds, so the renderer can't tell them apart.
// A dropped or stalled socket is reconnected (up to 3 tries, each with a connect timeout, all inside the
// Arena's 15 s grace) and the Arena resumes the same blob; the last picture stays on screen meanwhile. If the
// Arena no longer had the seat (WELCOME resumed = 0), the session ends with 'expired' instead of carrying on.
// My own cells are the exception (client-side prediction, as in most action netcode): they are drawn from the
// newest snapshot run forward by the round trip with my live input, so steering answers on the next frame instead
// of a round trip plus the interpolation delay later; a correction when a snapshot disagrees is eased out.
import { BOOST_MIN_MASS, BOOST_SPEED, INPUT_HZ, INTERP_DELAY_MS, WORLD, rad, speedOf } from '../shared/config';
import type { PowerUpType } from '../shared/config';
import type { Scene, SceneCell, ScenePellet, ScenePowerUp, SceneVirus, SimEvent } from '../shared/types';
import { decodeServer, encodeInput, encodePing, encodeRespawn, encodeView, pelletColor, qDelta } from '../shared/protocol';
import type { BoardMsg, NetCell, NetVirus, PelletsMsg, RosterEntry, SnapshotMsg } from '../shared/protocol';

const MAX_EXTRAP_MS = 50;
const PREDICT_MAX_MS = 350; // never run my cells further ahead than this (a long stall shows the last truth)
const PREDICT_STEP_S = 1 / 60;
const CORRECT_RATE = 12; // 1/s: how fast a prediction error is eased out
const CORRECT_SNAP = 250; // an error this big (teleport, respawn) is taken at once
const STREAM_DELAY_MS = 0; // pellet deltas and events: my blob is drawn ahead now, so don't hold them back
const INPUT_KEEPALIVE_MS = 250; // an unchanged INPUT is still resent this often
const PING_EVERY_MS = 2000;
const PELLET_GLIDE_MS = 100; // a moved pellet glides to its new spot over one PELLETS interval...
const PELLET_SNAP_DIST = 400; // ...unless it jumped this far
const MAX_SNAPS = 40; // 2 s of snapshots
const MAX_PENDING_EVENTS = 512;
export const STALL_MS = 3000; // no SNAPSHOT this long (after the WELCOME): the connection counts as lost
export const WATCH_EVERY_MS = 500;
export const CONNECT_TIMEOUT_MS = 2000; // a reconnect handshake still not open after this long counts as failed
// One per try. Worst case from the last SNAPSHOT to the last try giving up, with the watchdog's 500 ms steps:
// (STALL_MS + WATCH_EVERY_MS) + every delay + every (CONNECT_TIMEOUT_MS + WATCH_EVERY_MS) = 3.5 + 2.5 + 7.5
// = 13.5 s, inside the Arena's 15 s grace (tests/net.test.ts checks it).
export const RECONNECT_DELAYS_MS = [250, 750, 1500];
// offset tracking: samples this far below the offset, this many snapshots in a row, mean the server clock fell
// behind for good (a stall it didn't catch up, a longer route): jump to the fastest of them instead of drifting
const OFFSET_LOW_MS = 100, OFFSET_LOW_RUN = 10;
const SPAWN_REACH = 150; // a new cell grows out of its owner's nearest cell if that one is within r + r + this
const VIEW_EVERY_MS = 500; // a changing viewport (window being resized) goes out at most this often
// closes that mean "don't come back by yourself": normal, policy, replaced by another tab, room full, idle,
// joined another room
const NO_RETRY = new Set([1000, 1008, 4000, 4003, 4008, 4009]);

export interface OnlineHandlers {
  onWelcome?(myId: number): void;
  onClose?(reason: string): void; // gave up: the session is over
  onReconnecting?(on: boolean): void; // true when a lost socket is being replaced, false once the new one is in
}
export interface NetInput { dx: number; dy: number; split: boolean; eject: boolean; boost: boolean; aim?: boolean }
export interface NetRadarDot { x: number; y: number; r: number; ownerId: number }

interface Snap { ms: number; s: SnapshotMsg; cells: Map<number, NetCell>; viruses: Map<number, NetVirus>; byOwner: Map<number, NetCell[]> }
interface StorePellet extends ScenePellet { sx: number; sy: number; tx: number; ty: number; t0: number }

const clock = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());
const lerp = (a: number, b: number, k: number) => a + (b - a) * k;
const phase = (id: number) => ((id * 0.6180339887) % 1) * Math.PI * 2; // stable per-id shimmer/bob phase
const massOf = (r: number) => { const k = (r - 4) / 6; return k > 0 ? k * k : 0; }; // inverse of rad()
// the one of `from` nearest to c, if it's close enough to have just spawned c (split, virus pop, fed virus)
function parentOf<T extends { x: number; y: number; r: number }>(c: T, from: readonly T[] | undefined): T | null {
  let best: T | null = null, bd = Infinity;
  if (from) for (const o of from) {
    const d = Math.hypot(o.x - c.x, o.y - c.y);
    if (d < bd && d <= o.r + c.r + SPAWN_REACH) { bd = d; best = o; }
  }
  return best;
}

export class OnlineSession {
  rttMs = 0; // smoothed round trip, from PING/PONG
  readonly room: string;
  private readonly handlers: OnlineHandlers;
  private ws: WebSocket | null = null;
  private opened = false;
  private closedByUs = false;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private watchTimer: ReturnType<typeof setInterval> | null = null;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private welcomed = false; // a WELCOME arrived on this session (reconnects only happen after one)
  private retries = 0; // reconnect tries since the last WELCOME
  private lossReason = ''; // why the socket was first lost, reported if the tries run out
  private lastSnapAt = 0; // clock() of the last SNAPSHOT (or WELCOME, or a reconnect's open); 0 = not watching yet
  private openAt = 0; // clock() when the current socket was created
  private view = { w: 0, h: 0 }; // my viewport (CSS px), for the Arena's interest box
  private sentView = -1;
  private lastViewAt = -Infinity;
  private radar: NetRadarDot[] = [];
  private myId = -1;
  private roomName = '';
  private roster = new Map<number, RosterEntry>();
  private humans = 0;
  private board: BoardMsg = { myRank: -1, rows: [] };
  // snapshot playback
  private snaps: Snap[] = [];
  private offset = 0; // server ms - local ms, tracking the fastest recent arrivals
  private synced = false;
  private lowRun = 0; // snapshots in a row whose sample was well below the offset...
  private lowBest = -Infinity; // ...and the highest (fastest) of their samples
  private lastRaw = -1; // u32 serverMs unwrapped into lastMs
  private lastMs = 0;
  private lastRt = -Infinity; // scene clock (Scene.now): never runs backwards
  private lastPt = -Infinity; // playback time: never backwards either, and never parked past the newest snapshot + 50 ms
  // pellets: deltas and events are held back by INTERP_DELAY_MS too, so food vanishes when the cell gets there
  private pellets = new Map<number, StorePellet>();
  private moving = new Set<StorePellet>();
  private pelletList: ScenePellet[] = [];
  private pelletsDirty = false;
  private pelletQueue: { at: number; d: PelletsMsg }[] = [];
  private eventQueue: { at: number; e: SimEvent[] }[] = [];
  private pendingEvents = 0;
  // input throttle
  private lastSentAt = -Infinity;
  private sent = { dx: NaN, dy: NaN, eject: false, boost: false, aim: false };
  private splitLatch = false;
  // prediction: the live controls, the newest snapshot it is based on, and the eased-out error per cell
  private live = { dx: 0, dy: 0, boost: false };
  private predBase: Snap | null = null;
  private predErr = new Map<number, { x: number; y: number }>();
  private lastSceneAt = -Infinity;
  predict = true;

  constructor(room: string, handlers: OnlineHandlers = {}) {
    this.room = room;
    this.handlers = handlers;
  }

  connect(): void {
    if (this.ws || this.retryTimer) return;
    this.reset();
    this.closedByUs = false; this.welcomed = false; this.retries = 0; this.lossReason = '';
    this.openSocket();
  }
  // leaving on purpose: a normal close (1000) tells the Arena not to hold my seat
  close(): void {
    this.closedByUs = true;
    this.stopPing();
    this.stopWatch();
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = null;
    const ws = this.ws;
    this.ws = null;
    if (ws) { try { ws.close(1000); } catch { /* already closed */ } }
  }
  // my viewport in CSS px (call it every frame; it goes out on change, throttled, and after every (re)connect)
  setView(w: number, h: number): void {
    const W = Math.round(w), H = Math.round(h);
    if (!(W >= 1 && H >= 1)) return;
    this.view.w = Math.min(W, 0xffff); this.view.h = Math.min(H, 0xffff);
    this.flushView(false);
  }
  // Contract 2: nothing held, no steering (the blob heads for its own centre). For a tab being hidden or
  // closed; the Arena also goes neutral by itself once INPUT has been quiet for 1 s.
  sendNeutral(): void {
    this.splitLatch = false;
    const ws = this.ws;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    this.send(encodeInput({ dx: 0, dy: 0, split: false, eject: false, boost: false }));
    this.lastSentAt = clock();
    const last = this.sent;
    last.dx = 0; last.dy = 0; last.eject = false; last.boost = false; last.aim = false;
    this.live.dx = 0; this.live.dy = 0; this.live.boost = false;
  }

  // Called every frame with the latest controls; sends at most INPUT_HZ, only on change or every 250 ms.
  // A split press is latched until it has gone out, so a tap between two sends is never lost.
  sendInput(i: NetInput): void {
    if (i.split) this.splitLatch = true;
    const lv = this.live;
    lv.dx = Number.isFinite(i.dx) ? i.dx : 0; lv.dy = Number.isFinite(i.dy) ? i.dy : 0; lv.boost = !!i.boost;
    const ws = this.ws;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    const t = clock();
    if (t - this.lastSentAt < 1000 / INPUT_HZ - 2) return;
    const dx = qDelta(i.dx), dy = qDelta(i.dy), eject = !!i.eject, boost = !!i.boost, aim = !!i.aim, last = this.sent;
    const changed = this.splitLatch || dx !== last.dx || dy !== last.dy || eject !== last.eject || boost !== last.boost || aim !== last.aim;
    if (!changed && t - this.lastSentAt < INPUT_KEEPALIVE_MS) return;
    this.send(encodeInput({ dx, dy, split: this.splitLatch, eject, boost, aim }));
    this.splitLatch = false;
    this.lastSentAt = t;
    last.dx = dx; last.dy = dy; last.eject = eject; last.boost = boost; last.aim = aim;
  }
  respawn(): void {
    this.splitLatch = false;
    this.send(encodeRespawn());
  }
  // sim events for this player (and room-wide ones), released in step with the interpolated picture
  drainEvents(): SimEvent[] {
    const t = clock(), out: SimEvent[] = [];
    while (this.eventQueue.length && this.eventQueue[0].at <= t) {
      const batch = this.eventQueue.shift();
      if (batch) { this.pendingEvents -= batch.e.length; for (const e of batch.e) out.push(e); }
    }
    return out;
  }

  // ========== socket ==========
  private send(msg: ArrayBuffer): void {
    const ws = this.ws;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    try { ws.send(msg); } catch { /* the close handler reports it */ }
  }
  private ping(): void { this.send(encodePing(Math.floor(clock()) >>> 0)); }
  private stopPing(): void {
    if (this.pingTimer) clearInterval(this.pingTimer);
    this.pingTimer = null;
  }
  private stopWatch(): void {
    if (this.watchTimer) clearInterval(this.watchTimer);
    this.watchTimer = null;
  }
  private openSocket(): void {
    this.opened = false; this.lastSnapAt = 0; this.openAt = clock();
    let ws: WebSocket;
    try {
      const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
      ws = new WebSocket(`${proto}//${location.host}/ws/${encodeURIComponent(this.room)}`);
    } catch {
      this.lost(0, 'connection failed');
      return;
    }
    ws.binaryType = 'arraybuffer';
    // every handler checks it still belongs to the current socket: a reconnect must not hear the old one
    ws.onopen = () => {
      if (this.ws !== ws) return;
      this.opened = true;
      if (this.welcomed) this.lastSnapAt = clock(); // a reconnect: open but silent counts as a stall too
      this.ping();
      this.stopPing();
      this.pingTimer = setInterval(() => this.ping(), PING_EVERY_MS);
      this.sentView = -1;
      this.flushView(true);
    };
    ws.onmessage = ev => { if (this.ws === ws) this.onMessage(ev.data); };
    ws.onclose = ev => { if (this.ws === ws) this.onSocketClose(ev.code, ev.reason); };
    ws.onerror = () => { /* a close event always follows */ };
    this.ws = ws;
    if (!this.watchTimer) this.watchTimer = setInterval(() => this.watch(), WATCH_EVERY_MS);
  }
  private onSocketClose(code: number, reason: string): void {
    this.stopPing();
    this.ws = null;
    if (this.closedByUs) return;
    this.lost(code, reason || (this.opened ? 'disconnected' : 'connection failed'));
  }
  // The socket is gone. After a WELCOME, and unless the server said not to come back, try again a few
  // times; otherwise (or once the tries run out) the session is over.
  private lost(code: number, reason: string): void {
    if (this.closedByUs) return;
    if (this.welcomed && !NO_RETRY.has(code) && this.retries < RECONNECT_DELAYS_MS.length) {
      if (!this.retries) { this.lossReason = reason; this.handlers.onReconnecting?.(true); }
      const delay = RECONNECT_DELAYS_MS[this.retries++];
      this.retryTimer = setTimeout(() => { this.retryTimer = null; if (!this.closedByUs) this.openSocket(); }, delay);
      return;
    }
    this.stopWatch();
    this.handlers.onClose?.(NO_RETRY.has(code) || !this.lossReason ? reason : this.lossReason);
  }
  // No SNAPSHOT for STALL_MS on an open socket: drop it and reconnect. A reconnect whose handshake hangs (SYNs
  // blackholed by Wi-Fi without internet) is given up after CONNECT_TIMEOUT_MS, so the tries keep moving inside
  // the Arena's grace. (A first connection has main.ts's join timeout instead.)
  private watch(): void {
    const ws = this.ws;
    if (!ws) return;
    if (ws.readyState === WebSocket.CONNECTING) {
      if (!this.welcomed || clock() - this.openAt <= CONNECT_TIMEOUT_MS) return;
      this.ws = null;
      try { ws.close(); } catch { /* never opened */ }
      this.lost(4002, 'connection failed');
      return;
    }
    if (!this.lastSnapAt || clock() - this.lastSnapAt <= STALL_MS) return;
    this.ws = null;
    this.stopPing();
    try { ws.close(4001, 'stalled'); } catch { /* already closed */ }
    this.lost(4001, 'stalled');
  }
  private flushView(force: boolean): void {
    const v = this.view;
    if (!v.w || !v.h) return;
    const key = v.w * 0x10000 + v.h, t = clock();
    if (key === this.sentView || (!force && t - this.lastViewAt < VIEW_EVERY_MS)) return;
    const ws = this.ws;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    this.send(encodeView(v));
    this.sentView = key; this.lastViewAt = t;
  }
  private reset(): void {
    this.myId = -1; this.roomName = ''; this.roster.clear(); this.humans = 0; this.board = { myRank: -1, rows: [] };
    this.snaps = []; this.synced = false; this.lastRaw = -1; this.lastMs = 0; this.lastRt = -Infinity; this.lastPt = -Infinity;
    this.lowRun = 0; this.lowBest = -Infinity;
    this.predBase = null; this.predErr.clear(); this.lastSceneAt = -Infinity;
    this.freshStreams();
  }
  // what a new socket starts over: the Arena sends it every pellet in view again, and its own events
  private freshStreams(): void {
    this.pellets.clear(); this.moving.clear(); this.pelletList = []; this.pelletsDirty = true;
    this.pelletQueue = []; this.eventQueue = []; this.pendingEvents = 0; this.radar = [];
    this.lastSentAt = -Infinity; this.sent = { dx: NaN, dy: NaN, eject: false, boost: false, aim: false }; this.splitLatch = false;
  }
  private onMessage(data: unknown): void {
    const m = decodeServer(data);
    if (!m) return; // malformed: ignore it
    const at = clock();
    this.applyDue(at);
    switch (m.t) {
      case 'welcome': {
        const back = this.welcomed;
        if (back && !m.resumed) {
          // the Arena made a new seat: mine is gone (its grace ran out, or the room restarted), and with it that
          // life (paid out by the Arena). Leave (1000 releases the new seat) rather than swoop to a fresh blob.
          this.close();
          this.handlers.onClose?.('expired');
          return;
        }
        if (back) this.freshStreams(); // a reconnect: keep the picture, start the streams over
        this.welcomed = true; this.retries = 0; this.lossReason = ''; this.lastSnapAt = at;
        this.myId = m.myId; this.roomName = m.room; this.splitLatch = false;
        this.handlers.onWelcome?.(m.myId);
        if (back) this.handlers.onReconnecting?.(false);
        break;
      }
      case 'roster': {
        this.roster = new Map(m.roster.map(e => [e.id, e]));
        let h = 0;
        for (const e of m.roster) if (!e.bot) h++;
        this.humans = h;
        break;
      }
      case 'snapshot': this.lastSnapAt = at; this.onSnapshot(m.snap, at); break;
      case 'radar': this.radar = m.dots.map(d => ({ x: d.x, y: d.y, r: d.r, ownerId: d.owner })); break;
      case 'pellets': this.pelletQueue.push({ at: at + STREAM_DELAY_MS, d: m.delta }); break;
      case 'events':
        this.eventQueue.push({ at: at + STREAM_DELAY_MS, e: m.events });
        this.pendingEvents += m.events.length;
        while (this.pendingEvents > MAX_PENDING_EVENTS && this.eventQueue.length > 1) {
          const old = this.eventQueue.shift();
          if (old) this.pendingEvents -= old.e.length;
        }
        break;
      case 'board': this.board = m.board; break;
      case 'pong': {
        const rtt = (Math.floor(at) - m.time) >>> 0;
        if (rtt < 60000) this.rttMs = this.rttMs ? this.rttMs * 0.7 + rtt * 0.3 : rtt;
        break;
      }
    }
  }
  private unwrap(raw: number): number {
    if (this.lastRaw < 0) { this.lastRaw = raw; this.lastMs = raw; return raw; }
    let d = (raw - this.lastRaw) >>> 0;
    if (d >= 0x80000000) d -= 0x100000000; // went backwards
    this.lastRaw = raw;
    this.lastMs += d;
    return this.lastMs;
  }
  private onSnapshot(s: SnapshotMsg, at: number): void {
    const ms = this.unwrap(s.serverMs);
    const last = this.snaps[this.snaps.length - 1];
    if (last && ms <= last.ms) {
      if (ms > last.ms - 1000) return; // stale
      this.snaps = []; this.synced = false; this.lastRt = -Infinity; this.lastPt = -Infinity; // server clock restarted: start over
    }
    // the fastest arrivals carry the truest offset: follow them up quickly, drift down slowly (one late packet
    // must not drag playback back), but a sustained drop is real: jump to the best of the low run
    const sample = ms - at;
    if (!this.synced || Math.abs(sample - this.offset) > 1000) { this.offset = sample; this.synced = true; this.lowRun = 0; }
    else if (sample < this.offset - OFFSET_LOW_MS) {
      this.lowBest = this.lowRun ? Math.max(this.lowBest, sample) : sample;
      if (++this.lowRun >= OFFSET_LOW_RUN) { this.offset = this.lowBest; this.lowRun = 0; }
      else this.offset += (sample - this.offset) * 0.02;
    } else {
      this.lowRun = 0;
      this.offset += (sample - this.offset) * (sample > this.offset ? 0.25 : 0.02);
    }
    const byOwner = new Map<number, NetCell[]>();
    for (const c of s.cells) { const l = byOwner.get(c.owner); if (l) l.push(c); else byOwner.set(c.owner, [c]); }
    this.snaps.push({ ms, s, cells: new Map(s.cells.map(c => [c.id, c])), viruses: new Map(s.viruses.map(v => [v.id, v])), byOwner });
    if (this.snaps.length > MAX_SNAPS) this.snaps.shift();
  }

  // ========== pellets ==========
  private applyDue(t: number): void {
    while (this.pelletQueue.length && this.pelletQueue[0].at <= t) {
      const q = this.pelletQueue.shift();
      if (q) this.applyPellets(q.d, t);
    }
  }
  // removes first: an id freed by this delta may come straight back as an add
  private applyPellets(d: PelletsMsg, t: number): void {
    for (const id of d.removes) {
      const p = this.pellets.get(id);
      if (p) { this.pellets.delete(id); this.moving.delete(p); }
    }
    for (const a of d.adds) {
      const old = this.pellets.get(a.id);
      if (old) this.moving.delete(old);
      this.pellets.set(a.id, {
        id: a.id, x: a.x, y: a.y, r: a.r, color: pelletColor(a.color) ?? '#ffffff', kind: a.kind, ph: phase(a.id),
        sx: a.x, sy: a.y, tx: a.x, ty: a.y, t0: t,
      });
    }
    for (const m of d.moves) {
      const p = this.pellets.get(m.id);
      if (!p) continue;
      if (Math.abs(m.x - p.x) + Math.abs(m.y - p.y) > PELLET_SNAP_DIST) {
        p.x = p.sx = p.tx = m.x; p.y = p.sy = p.ty = m.y;
        this.moving.delete(p);
      } else {
        p.sx = p.x; p.sy = p.y; p.tx = m.x; p.ty = m.y; p.t0 = t;
        this.moving.add(p);
      }
    }
    if (d.adds.length || d.removes.length) this.pelletsDirty = true;
  }
  private glide(t: number): void {
    for (const p of this.moving) {
      const k = Math.min(1, Math.max(0, (t - p.t0) / PELLET_GLIDE_MS));
      p.x = lerp(p.sx, p.tx, k); p.y = lerp(p.sy, p.ty, k);
      if (k >= 1) this.moving.delete(p);
    }
  }

  // ========== scene ==========
  // nowMs: the performance.now() timeline (a requestAnimationFrame timestamp is fine). null until the
  // first WELCOME and SNAPSHOT have arrived.
  scene(nowMs: number): Scene | null {
    const real = clock(), t = Math.abs(nowMs - real) < 1000 ? nowMs : real;
    this.applyDue(t);
    if (this.myId < 0 || !this.snaps.length) return null;
    this.glide(t);
    if (this.pelletsDirty) { this.pelletList = Array.from(this.pellets.values()); this.pelletsDirty = false; }

    const target = t + this.offset - INTERP_DELAY_MS;
    const rt = Math.max(this.lastRt, target);
    this.lastRt = rt;
    // Playback runs on its own clock, capped at what the newest snapshot can show (MAX_EXTRAP_MS past it): when
    // data stops (a stall, or the offset was too high) the picture parks there, and resumes from there, instead
    // of from a clock that kept running ahead of every snapshot.
    const pt = Math.min(Math.max(this.lastPt, target), this.snaps[this.snaps.length - 1].ms + MAX_EXTRAP_MS);
    this.lastPt = pt;
    // keep one snapshot at or before pt, and everything after it
    while (this.snaps.length > 2 && this.snaps[1].ms <= pt) this.snaps.shift();
    const snaps = this.snaps, n = snaps.length, first = snaps[0];
    let a = first, b = first, k = 1;
    if (n > 1 && pt > first.ms) {
      if (pt < snaps[1].ms) { b = snaps[1]; k = (pt - first.ms) / (b.ms - first.ms); }
      else { a = snaps[n - 2]; b = snaps[n - 1]; k = 1 + Math.min(pt - b.ms, MAX_EXTRAP_MS) / Math.max(1, b.ms - a.ms); }
    }
    const kr = Math.min(k, 1); // sizes are interpolated, never extrapolated

    // Between two different snapshots (a !== b), what one of them lacks is eased too: a cell new in b (a split
    // half, a virus-pop piece) flies out of its owner's nearest cell in a, and a cell gone from b (eaten, merged,
    // out of view) shrinks away at its a position, instead of popping up where it will be or vanishing early.
    const between = a !== b, ks = Math.min(k, 1);
    const cells: SceneCell[] = [], myCells: SceneCell[] = [];
    const cell = (id: number, owner: number, x: number, y: number, r: number, flags: number) => {
      const o = this.roster.get(owner);
      const c: SceneCell = { id, ownerId: owner, x, y, r, mass: massOf(r), skin: o ? o.skin : 'blue', name: o ? o.name : '', flags };
      cells.push(c);
      if (owner === this.myId) myCells.push(c);
    };
    for (const cb of b.s.cells) {
      const ca0 = a.cells.get(cb.id), ca = ca0 && ca0.owner === cb.owner ? ca0 : undefined; // ids get reused
      if (ca) { cell(cb.id, cb.owner, lerp(ca.x, cb.x, k), lerp(ca.y, cb.y, k), lerp(ca.r, cb.r, kr), cb.flags); continue; }
      const from = between ? parentOf(cb, a.byOwner.get(cb.owner)) : null;
      if (from) cell(cb.id, cb.owner, lerp(from.x, cb.x, ks), lerp(from.y, cb.y, ks), cb.r, cb.flags);
      else cell(cb.id, cb.owner, cb.x, cb.y, cb.r, cb.flags);
    }
    if (between && k < 1) {
      for (const ca of a.s.cells) {
        if (b.cells.has(ca.id)) continue; // (an id reused by another owner: the renderer keys cells by id, so never both)
        const r = lerp(ca.r, 0, kr);
        if (r > 0.5) cell(ca.id, ca.owner, ca.x, ca.y, r, ca.flags);
      }
    }
    const viruses: SceneVirus[] = [];
    for (const vb of b.s.viruses) {
      const va = a.viruses.get(vb.id);
      if (va) { viruses.push({ id: vb.id, x: lerp(va.x, vb.x, k), y: lerp(va.y, vb.y, k), r: lerp(va.r, vb.r, kr) }); continue; }
      const from = between ? parentOf(vb, a.s.viruses) : null; // a fed virus shoots a new one out of itself
      viruses.push(from ? { id: vb.id, x: lerp(from.x, vb.x, ks), y: lerp(from.y, vb.y, ks), r: vb.r } : { id: vb.id, x: vb.x, y: vb.y, r: vb.r });
    }
    if (between && k < 1) {
      for (const va of a.s.viruses) {
        if (b.viruses.has(va.id)) continue;
        const r = lerp(va.r, 0, kr);
        if (r > 0.5) viruses.push({ id: va.id, x: va.x, y: va.y, r });
      }
    }
    const powerups: ScenePowerUp[] = b.s.powerups.map(u => ({
      id: u.id, type: u.type, x: u.x, y: u.y, ph: phase(u.id), born: (b.ms - u.ageMs) / 1000,
    }));
    // timers were sampled at b; count them on to the picture's time
    const ahead = (b.ms - pt) / 1000;
    const myFx: Partial<Record<PowerUpType, number>> = {};
    for (const f of b.s.fx) { const left = f.remaining + ahead; if (left > 0) myFx[f.type] = left; }
    const e = b.s.event;
    const event = e ? { type: e.type, remaining: Math.max(0, e.remaining + ahead), dur: e.dur } : null;
    if (this.predict && this.snaps.length) this.predictMine(cells, myCells, t);
    const alive = b.s.myAlive;
    const myMass = alive && a.s.myAlive ? lerp(a.s.myMass, b.s.myMass, kr) : b.s.myMass;

    return {
      now: rt / 1000, myId: this.myId, alive,
      myCells, myMass, myFx, cells, pellets: this.pelletList, viruses, powerups,
      leaderboard: this.board.rows, myRank: this.board.myRank, event,
      online: true, roomName: this.roomName, humans: this.humans, radar: this.radar,
    };
  }

  // ========== prediction ==========
  // My cells of `base` (the newest snapshot) run forward to the time my input sent now reaches the Arena: the
  // same steering rule as Sim.step (head for centre + input delta at speedOf(mass), slowing near the target),
  // plus any split launch velocity seen between the last two snapshots, decaying like the Sim's.
  private run(base: Snap, prev: Snap | undefined, horizonMs: number): Map<number, { x: number; y: number; r: number }> {
    const mine = base.byOwner.get(this.myId) ?? [], out = new Map<number, { x: number; y: number; r: number; vx: number; vy: number; m: number }>();
    const fx = base.s.fx.some(f => f.type === 'speed' && f.remaining > 0) ? 1.6 : 1;
    const fast = fx * (this.live.boost && base.s.myMass > BOOST_MIN_MASS ? BOOST_SPEED : 1);
    const span = prev ? Math.max(1, base.ms - prev.ms) / 1000 : 0;
    for (const c of mine) {
      const m = massOf(c.r), p = prev?.cells.get(c.id);
      let vx = 0, vy = 0;
      if (p && p.owner === c.owner && span) {
        const sx = (c.x - p.x) / span, sy = (c.y - p.y) / span;
        if (Math.hypot(sx, sy) > speedOf(Math.max(m, 1)) * fast * 1.35) { vx = sx; vy = sy; } // flying out of a split
      }
      out.set(c.id, { x: c.x, y: c.y, r: c.r, vx, vy, m });
    }
    let left = Math.min(Math.max(horizonMs, 0), PREDICT_MAX_MS) / 1000;
    while (left > 1e-4 && out.size) {
      const dt = Math.min(PREDICT_STEP_S, left); left -= dt;
      let mx = 0, my = 0, mm = 0;
      for (const c of out.values()) { mx += c.x * c.m; my += c.y * c.m; mm += c.m; }
      if (mm <= 0) break;
      const tx = mx / mm + this.live.dx, ty = my / mm + this.live.dy, decay = Math.exp(-5 * dt);
      for (const c of out.values()) {
        const r = rad(c.m), dx = tx - c.x, dy = ty - c.y, d = Math.hypot(dx, dy) || 1;
        const sp = speedOf(Math.max(c.m, 1)) * fast * Math.min(1, d / (r * 0.6 + 20));
        c.x += (dx / d * sp + c.vx) * dt; c.y += (dy / d * sp + c.vy) * dt;
        c.vx *= decay; c.vy *= decay;
        const e = Math.min(r * 0.5, WORLD / 2);
        c.x = Math.min(Math.max(c.x, e), WORLD - e); c.y = Math.min(Math.max(c.y, e), WORLD - e);
      }
    }
    return out;
  }
  private predictMine(cells: SceneCell[], myCells: SceneCell[], t: number): void {
    const n = this.snaps.length, base = this.snaps[n - 1], prev = n > 1 ? this.snaps[n - 2] : undefined;
    if (!base.s.myAlive) { this.predBase = null; this.predErr.clear(); return; }
    // the Arena's clock now is about t + offset (newest arrival) + the downlink; my input lands an uplink later
    const horizon = t + this.offset + this.rttMs - base.ms;
    const pred = this.run(base, prev, horizon);
    const dtS = Math.min(0.1, Math.max(0, (t - this.lastSceneAt) / 1000));
    this.lastSceneAt = t;
    const decay = Math.exp(-CORRECT_RATE * dtS);
    // a new base: what the old one predicts for this same moment minus what the new one does is the correction
    if (this.predBase && this.predBase !== base) {
      const oldIdx = this.snaps.indexOf(this.predBase);
      const old = this.run(this.predBase, oldIdx > 0 ? this.snaps[oldIdx - 1] : undefined, t + this.offset + this.rttMs - this.predBase.ms);
      for (const [id, q] of pred) {
        const o = old.get(id);
        if (!o) continue;
        const e = this.predErr.get(id) ?? { x: 0, y: 0 };
        e.x += o.x - q.x; e.y += o.y - q.y;
        if (Math.hypot(e.x, e.y) > CORRECT_SNAP) { e.x = 0; e.y = 0; }
        this.predErr.set(id, e);
      }
    }
    this.predBase = base;
    for (const id of this.predErr.keys()) if (!pred.has(id)) this.predErr.delete(id);
    // swap my interpolated cells for the predicted ones (a cell only in the older snapshot shrinking away goes)
    for (let i = cells.length - 1; i >= 0; i--) if (cells[i].ownerId === this.myId) cells.splice(i, 1);
    const mine = myCells.splice(0, myCells.length), byId = new Map(mine.map(c => [c.id, c]));
    for (const [id, q] of pred) {
      const e = this.predErr.get(id);
      if (e) { e.x *= decay; e.y *= decay; }
      const src = byId.get(id), o = this.roster.get(this.myId);
      const c: SceneCell = src
        ? { ...src, x: q.x + (e?.x ?? 0), y: q.y + (e?.y ?? 0), r: src.r }
        : { id, ownerId: this.myId, x: q.x + (e?.x ?? 0), y: q.y + (e?.y ?? 0), r: q.r, mass: massOf(q.r), skin: o ? o.skin : 'blue', name: o ? o.name : '', flags: base.cells.get(id)?.flags ?? 0 };
      cells.push(c); myCells.push(c);
    }
  }
}
